import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminGrantApi } from '../lib/api'
import type { AdminGrantKind, BlockContactRequest } from '../types/adminGrant'
import type { RevokeCanvasserGrantRequest } from '../types/canvasserGrant'

// LW-24 Slice 3: React Query surface over the merged /api/admin/grants controller. Keyed by kind
// so the two grids cache independently; person-level block/unblock invalidates the whole
// ['admin','grants'] subtree because it can change rows in BOTH grids at once.
const ROOT_KEY = ['admin', 'grants'] as const
const listKey = (kind: AdminGrantKind) => [...ROOT_KEY, kind] as const

export function useAdminCanvasserGrantList(campaignId: string | undefined) {
  return useQuery({
    queryKey: [...listKey('canvasser'), campaignId ?? 'all'],
    queryFn: async () => (await adminGrantApi.listCanvassers(campaignId)).data,
  })
}

export function useAdminAttendeeGrantList(eventId: string | undefined) {
  return useQuery({
    queryKey: [...listKey('attendee'), eventId ?? 'all'],
    queryFn: async () => (await adminGrantApi.listAttendees(eventId)).data,
  })
}

export function useBlockContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, reasonNote }: { contactId: string } & BlockContactRequest) =>
      adminGrantApi.block(contactId, { reasonNote }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ROOT_KEY }) },
  })
}

export function useUnblockContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) => adminGrantApi.unblock(contactId).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ROOT_KEY }) },
  })
}

export function useAdminGrantSessions(kind: AdminGrantKind, grantId: string | undefined) {
  return useQuery({
    queryKey: [...listKey(kind), grantId, 'sessions'],
    queryFn: async () => {
      if (!grantId) throw new Error('Grant ID required')
      const fetch = kind === 'canvasser' ? adminGrantApi.canvasserSessions : adminGrantApi.attendeeSessions
      return (await fetch(grantId)).data
    },
    enabled: !!grantId,
  })
}

export function useAdminRevokeGrant(kind: AdminGrantKind) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ grantId, body }: { grantId: string; body: RevokeCanvasserGrantRequest }) => {
      const revoke = kind === 'canvasser' ? adminGrantApi.revokeCanvasserGrant : adminGrantApi.revokeAttendeeGrant
      return revoke(grantId, body).then(r => r.data)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: listKey(kind) }) },
  })
}

export function useAdminRevokeSession(kind: AdminGrantKind) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ grantId, sessionId }: { grantId: string; sessionId: string }) => {
      const revoke = kind === 'canvasser' ? adminGrantApi.revokeCanvasserSession : adminGrantApi.revokeAttendeeSession
      return revoke(grantId, sessionId).then(r => r.data)
    },
    onSuccess: (_data, { grantId }) => {
      qc.invalidateQueries({ queryKey: listKey(kind) })
      qc.invalidateQueries({ queryKey: [...listKey(kind), grantId, 'sessions'] })
    },
  })
}
